import {
  benefitOnDate,
  eligibleForSpousalBenefit,
  MAX_BENEFIT_AGE_MONTHS,
  spousalBenefitOnDate,
  survivorBenefit,
} from '$lib/benefit-calculator';
import { Money } from '$lib/money';
import { MonthDate, MonthDuration } from '$lib/month-time';
import type { Recipient } from '$lib/recipient';
import {
  type AlreadyFiled,
  NOT_FILED,
  validateFiledMonth,
} from './already-filed';
import { BenefitPeriod, BenefitType } from './benefit-period.js';
import { classifyEarnerDependent } from './earner-dependent.js';
import { PersonalBenefitPeriods } from './recipient-personal-benefits.js';

/**
 * Computes the benefit periods (personal, spousal, and survivor) for a
 * couple's Social Security filing strategy.
 *
 * @param recipients - The two recipients.
 * @param finalDates - Final dates (death dates) for each recipient.
 * @param strats - Filing ages (as MonthDuration) for each recipient.
 * @returns The benefit periods with amounts, date ranges, and types.
 */
export function strategySumPeriodsCouple(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  strats: [MonthDuration, MonthDuration]
): BenefitPeriod[] {
  const periods: BenefitPeriod[] = [];

  // Declare variables to hold recipient information, final dates, filing
  // strategies (ages), and calculated filing dates.
  let earner: Recipient;
  let dependent: Recipient;
  let earnerFinalDate: MonthDate;
  let dependentFinalDate: MonthDate;
  let earnerStrat: MonthDuration;
  let dependentStrat: MonthDuration;
  let earnerStratDate: MonthDate;
  let dependentStratDate: MonthDate;

  // Calculate the MonthDate version of the filing strategy ages by offsetting
  // the strategy ages from each recipient's birthdate.
  const stratDates: [MonthDate, MonthDate] = [null, null];
  for (let i = 0; i < 2; i++) {
    stratDates[i] = recipients[i].birthdate.dateAtSsaAge(strats[i]);
  }

  // Determine the higher and lower earner based on their Primary Insurance
  // Amount (PIA).
  const { earnerIndex, dependentIndex } = classifyEarnerDependent(recipients);
  earner = recipients[earnerIndex];
  dependent = recipients[dependentIndex];
  earnerFinalDate = finalDates[earnerIndex];
  dependentFinalDate = finalDates[dependentIndex];
  earnerStrat = strats[earnerIndex];
  dependentStrat = strats[dependentIndex];
  earnerStratDate = stratDates[earnerIndex];
  dependentStratDate = stratDates[dependentIndex];

  // If the dependent has 0 PIA, then they can't file earlier than the earner.
  // Move the dependent's filing date up to the earner's:
  if (
    dependent.pia().primaryInsuranceAmount().cents() === 0 &&
    dependentStratDate.lessThan(earnerStratDate)
  ) {
    dependentStrat = earnerStrat;
    dependentStratDate = earnerStratDate;
  }

  // Determine the start date for survivor benefits. This is the later of:
  // 1. The month after the earner's death date.
  // 2. The dependent's filing date.
  const survivorStartDate = MonthDate.max(
    earnerFinalDate.addDuration(new MonthDuration(1)),
    dependentStratDate
  );

  // Determine the dependent's survivor benefit amount:
  let isSurvivorBenefitApplicable = false;
  let survivorBenefitAmount: Money = Money.zero();
  if (dependentFinalDate.greaterThan(survivorStartDate)) {
    survivorBenefitAmount = survivorBenefit(
      /*survivor*/ dependent,
      /*deceased*/ earner,
      /*deceasedFilingDate*/ earnerStratDate,
      /*deceasedDeathDate*/ earnerFinalDate,
      /*survivorFilingDate*/ survivorStartDate
    );
    // Note that if the survivor's personal benefit is greater than their
    // survivor benefit, they will not switch to a survivor benefit.
    const dependentFinalPersonalBenefit = benefitOnDate(
      dependent,
      /*filingDate*/ dependentStratDate,
      // Add a year to include all late filing credits.
      /*atDate*/ dependentStratDate.addDuration(MonthDuration.OneYear())
    );
    if (dependentFinalPersonalBenefit.cents() < survivorBenefitAmount.cents()) {
      isSurvivorBenefitApplicable = true;
    }
  }

  // Earner's Personal Benefit:
  // Earner is simple as they will never have spousal or survivor benefits:
  PersonalBenefitPeriods(
    earner,
    earner.birthdate.dateAtSsaAge(earnerStrat),
    earnerFinalDate,
    periods,
    earnerIndex
  );
  // The latest that the dependent will be collecting a personal benefit is the
  // month before starting a survivor benefit.
  let dependentFinalPersonalDate = dependentFinalDate;
  if (
    isSurvivorBenefitApplicable &&
    dependentFinalDate.greaterThanOrEqual(survivorStartDate)
  ) {
    dependentFinalPersonalDate = survivorStartDate.subtractDuration(
      new MonthDuration(1)
    );
  }
  // Dependent's Personal Benefit:
  PersonalBenefitPeriods(
    dependent,
    dependent.birthdate.dateAtSsaAge(dependentStrat),
    dependentFinalPersonalDate,
    periods,
    dependentIndex
  );

  // Dependent's Survivor Benefit:
  if (isSurvivorBenefitApplicable) {
    const survivorPeriod = new BenefitPeriod();
    survivorPeriod.amount = survivorBenefitAmount;
    survivorPeriod.startDate = survivorStartDate;
    survivorPeriod.endDate = dependentFinalDate;
    survivorPeriod.recipientIndex = dependentIndex;
    survivorPeriod.benefitType = BenefitType.Survivor;
    periods.push(survivorPeriod);
  }

  // Dependent's Spousal Benefit:
  if (eligibleForSpousalBenefit(dependent, earner)) {
    const spousalPeriod = new BenefitPeriod();
    // The start date for spousal benefits is the later of the two filing dates.
    spousalPeriod.startDate = MonthDate.max(
      earnerStratDate,
      dependentStratDate
    );
    // The end date for spousal benefits is the earlier of when the dependent
    // starts receiving survivor benefits or their own death date.
    spousalPeriod.endDate = MonthDate.min(
      survivorStartDate.subtractDuration(new MonthDuration(1)),
      dependentFinalDate
    );

    if (spousalPeriod.endDate.greaterThanOrEqual(spousalPeriod.startDate)) {
      spousalPeriod.amount = spousalBenefitOnDate(
        dependent,
        /*spouse=*/ earner,
        /*spouseFilingDate=*/ earnerStratDate,
        /*filingDate=*/ dependentStratDate,
        /*atDate=*/ spousalPeriod.startDate
      );
      spousalPeriod.recipientIndex = dependentIndex;
      spousalPeriod.benefitType = BenefitType.Spousal;
      periods.push(spousalPeriod);
    }
  }

  return periods;
}

/**
 * Converts an annual discount rate to a monthly discount rate.
 *
 * @param {number} annualDiscountRate - Annual discount rate (e.g., 0.03 for 3%).
 * @returns {number} Monthly discount rate.
 */
export function calculateMonthlyDiscountRate(
  annualDiscountRate: number
): number {
  if (annualDiscountRate === 0) {
    return 0;
  } else {
    return (1 + annualDiscountRate) ** (1 / 12) - 1;
  }
}

/**
 * Calculates NPV for a single period with optional memoization.
 * This helper can be used by both the original and optimized functions.
 */
function calculatePeriodNPV(
  monthlyPaymentCents: number,
  numberOfPayments: number,
  monthsToFirstPayment: number,
  monthlyDiscountRate: number,
  pvFactorCache?: Map<number, number>,
  discountFactorCache?: Map<number, number>
): number {
  if (monthlyDiscountRate === 0) {
    return monthlyPaymentCents * numberOfPayments;
  }

  let pvFactor: number;
  let discountFactorToCurrentDate: number;

  // Use memoization if caches are provided
  if (pvFactorCache && discountFactorCache) {
    pvFactor = getMemoizedPVFactor(
      numberOfPayments,
      monthlyDiscountRate,
      pvFactorCache
    );
    discountFactorToCurrentDate = getMemoizedDiscountFactor(
      monthsToFirstPayment,
      monthlyDiscountRate,
      discountFactorCache
    );
  } else {
    // Direct calculation for non-memoized case
    pvFactor =
      (1 - (1 + monthlyDiscountRate) ** -numberOfPayments) /
      monthlyDiscountRate;
    discountFactorToCurrentDate =
      (1 + monthlyDiscountRate) ** -monthsToFirstPayment;
  }

  return monthlyPaymentCents * pvFactor * discountFactorToCurrentDate;
}

/**
 * Calculates the net present value of all benefit periods.
 *
 * This function calls strategySumPeriods to get an array of benefit periods,
 * and then calculates the net present value of the payments during those periods,
 * accounting for the duration of each period and discounting future payments.
 *
 * @param {[Recipient, Recipient]} recipients - An array containing the two
 *                                              recipients for whom the strategy
 *                                              results are being calculated.
 * @param {[MonthDate, MonthDate]} finalDates - An array containing the final
 *                                              dates (death dates) for each
 *                                              recipient.
 * @param {MonthDate} currentDate - Today's date, used as the reference for NPV calculation.
 * @param {number} monthlyDiscountRate - Monthly discount rate (pre-calculated from annual rate).
 * @param {[MonthDuration, MonthDuration]} strats - An array containing the
 *                                                  filing ages (as
 *                                                  MonthDuration)
 *                                                  for each recipient.
 * @returns {Money} The net present value of all payments across all periods.
 */
export function strategySumTotalPeriodsCouple(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  monthlyDiscountRate: number,
  strats: [MonthDuration, MonthDuration]
): Money {
  const periods = strategySumPeriodsCouple(recipients, finalDates, strats);
  let totalNPVCents = 0;

  for (const period of periods) {
    const monthlyPaymentCents = period.amount.cents();

    // Determine the effective start and end dates for payments, considering currentDate
    // Payments are assumed to be received at the end of the month for the previous month's benefits.
    const firstPaymentDate = period.startDate.addDuration(new MonthDuration(1));
    const lastPaymentDate = period.endDate.addDuration(new MonthDuration(1));

    // The effective start of payments for NPV calculation is the later of currentDate + 1 month or firstPaymentDate
    const effectiveStartPaymentDate = MonthDate.max(
      currentDate.addDuration(new MonthDuration(1)),
      firstPaymentDate
    );
    const effectiveEndPaymentDate = lastPaymentDate;

    // If the effective start date is after the effective end date, no payments from this period contribute to NPV.
    if (effectiveStartPaymentDate.greaterThan(effectiveEndPaymentDate)) {
      continue;
    }

    const numberOfPayments =
      effectiveEndPaymentDate.monthsSinceEpoch() -
      effectiveStartPaymentDate.monthsSinceEpoch() +
      1;

    const monthsToFirstPayment =
      effectiveStartPaymentDate.monthsSinceEpoch() -
      currentDate.monthsSinceEpoch();

    totalNPVCents += calculatePeriodNPV(
      monthlyPaymentCents,
      numberOfPayments,
      monthsToFirstPayment,
      monthlyDiscountRate
    );
  }

  return Money.fromCents(totalNPVCents);
}

export function earliestFiling(
  recipient: Recipient,
  currentDate: MonthDate
): MonthDuration {
  // The earliest date that a recipient can file for benefits is governed by a
  // few rules:

  // 1) Cannot file before they are age 62 for an entire month. Essentially
  //    this means the month after their 62nd birthday, unless born on 1st or
  //    2nd of the month, then it's the month of their 62nd birthday.
  //    See: https://ssa.tools/guides/1st-and-2nd-of-month
  let earliestMonth = recipient.birthdate.earliestFilingMonth();

  const currentAge = recipient.birthdate.ageAtSsaDate(currentDate);

  // If that's now or in the future, done:
  if (earliestMonth.greaterThanOrEqual(currentAge)) {
    return earliestMonth;
  }

  // 2) You can only file retroactively back to your normal retirement age.
  if (recipient.normalRetirementAge().greaterThan(earliestMonth)) {
    // Though you can always file at the current age:
    if (recipient.normalRetirementAge().greaterThan(currentAge)) {
      earliestMonth = currentAge;
    } else {
      earliestMonth = recipient.normalRetirementAge();
    }
  }

  // 3) You can only file retroactively by at most 6 months.
  const sixMonthsAgo = currentAge.subtract(new MonthDuration(6));
  if (earliestMonth.lessThan(sixMonthsAgo)) {
    earliestMonth = sixMonthsAgo;
  }

  return earliestMonth;
}

/**
 * The oldest filing age worth considering, as a duration. Filing later than
 * the age at which delayed credits stop only forgoes payments, so the maximum
 * useful filing age and MAX_BENEFIT_AGE_MONTHS are by definition the same age.
 */
export const MAX_FILING_AGE: MonthDuration = new MonthDuration(
  MAX_BENEFIT_AGE_MONTHS
);

/**
 * The inclusive span of filing ages an optimizer should search over.
 *
 * `hasChoice` is false once `earliest` has reached 70, or when the recipient
 * has already filed (see `filingAgeRange`'s `filedAt`). Because SSA allows
 * filing up to six months retroactively, `earliest` lags the recipient's
 * current age by six months once they are past full retirement age, so the
 * age-based flip happens at age 70y6m rather than at 70y0m. A recipient aged
 * 70y3m still has a real, if narrow, range to search.
 *
 * Past that point exactly one option remains: file as soon as possible,
 * backdated to `earliest` — the most retroactive month SSA allows — so
 * `earliest` and `latest` are equal and the "optimization" is a formality.
 * Callers presenting results to a user should say so rather than implying a
 * decision was made on the recipient's behalf.
 *
 * Note that `latest` is NOT bounded by `MAX_FILING_AGE`: past 70 it exceeds
 * it, and for a filed recipient both bounds are the actual filing age,
 * whatever that is. The span is always non-empty (`earliest <= latest`).
 *
 * Model limitation: the NPV functions count payments from the month after
 * `currentDate`, so the retroactive lump sum SSA pays for a backdated claim
 * is worth $0 to the optimizer. Past full retirement age the winner among
 * the backdated ages is therefore predetermined (the amount either rises
 * with age or, past 70, is flat), and the narrow past-70y0m range is
 * searched but cannot surprise. Valuing those months as a lump at
 * `currentDate + 1` would let the "up to six months retroactively" the UI
 * mentions actually influence the recommendation; it is left for follow-up.
 */
export interface FilingAgeRange {
  readonly earliest: MonthDuration;
  readonly latest: MonthDuration;
  readonly hasChoice: boolean;
}

/**
 * The earliest death age worth modelling for a recipient: the later of their
 * current age and the earliest age they could still file.
 *
 * Below this there is no filing strategy to find — the recipient would have
 * died before they could claim anything — so asking the optimizer about such
 * a scenario is a malformed question rather than one with a degenerate
 * answer. Death-age buckets must start here.
 *
 * `Birthdate.currentAge()` is whole years and so cannot express this on its
 * own: someone aged 62y1m whose earliest filing month is 62y1m would get a
 * bucket at 62y0m, one month short of being able to file.
 */
export function earliestModelableDeathAge(
  recipient: Recipient,
  currentDate: MonthDate
): MonthDuration {
  const currentAge = recipient.birthdate.ageAtSsaDate(currentDate);
  const earliest = earliestFiling(recipient, currentDate);
  return currentAge.greaterThan(earliest) ? currentAge : earliest;
}

/**
 * Thrown when a recipient's death date precedes every filing age available to
 * them, so no filing strategy exists for the scenario.
 *
 * The optimizers used to return a `[MonthDuration(0), -1]` sentinel here — an
 * "answer" of "file at age 0 for minus one cent" that is indistinguishable,
 * to both the type system and the caller, from a real result. The UI wrote it
 * straight into the results grid, and a downstream filter quietly dropped it,
 * which is how the over-70 bug rendered an empty chart with no error. Callers
 * should avoid asking (do not model a death age earlier than the recipient
 * could file); reaching this means a scenario was built that cannot happen.
 */
export class NoFilingAgeAvailableError extends Error {
  constructor(startFilingAge: number, endFilingAge: number) {
    super(
      `no filing age available: the earliest filing age is ` +
        `${startFilingAge} months but the recipient's death allows filing ` +
        `only through ${endFilingAge} months`
    );
    this.name = 'NoFilingAgeAvailableError';
  }
}

/**
 * Computes the filing ages still available to a recipient as of `currentDate`.
 *
 * Any search that starts from `earliestFiling` must bound itself with this
 * rather than a hardcoded age 70: that starting age can exceed 70, leaving a
 * loop bounded by a literal 70 to run zero times, and any length derived from
 * the range (`end - start + 1`) to go negative and throw `RangeError` from the
 * typed-array allocations in the fast paths. (Searches that start from
 * `earliestFilingMonth`, the age-62 month, are not affected — see
 * alternative-strategies.ts and ai-export.ts.)
 *
 * `filedAt`, when given, is the month the recipient actually started
 * benefits. The range is then that single filing age with `hasChoice`
 * false: there is nothing to search. The month must pass
 * `validateFiledMonth`; the form enforces that, so a failure here is a
 * programming error and throws rather than silently searching a nonsense
 * age.
 */
export function filingAgeRange(
  recipient: Recipient,
  currentDate: MonthDate,
  filedAt: MonthDate | null = null
): FilingAgeRange {
  if (filedAt !== null) {
    const problem = validateFiledMonth(
      recipient.birthdate,
      filedAt,
      currentDate
    );
    if (problem !== null) {
      throw new Error(`invalid filed month: ${problem}`);
    }
    const filedAge = recipient.birthdate.ageAtSsaDate(filedAt);
    return {
      earliest: filedAge,
      latest: new MonthDuration(filedAge.asMonths()),
      hasChoice: false,
    };
  }

  const earliest = earliestFiling(recipient, currentDate);
  const hasChoice = earliest.lessThan(MAX_FILING_AGE);
  return {
    earliest,
    // A fresh instance rather than MAX_FILING_AGE itself: MonthDuration's
    // increment()/decrement() mutate in place, and that idiom is used
    // elsewhere, so handing out the shared constant by identity would let one
    // caller corrupt it for every later call.
    latest: hasChoice ? new MonthDuration(MAX_BENEFIT_AGE_MONTHS) : earliest,
    hasChoice,
  };
}

/**
 * Cache for memoizing expensive NPV calculations.
 */
interface NPVCache {
  pvFactorCache: Map<number, number>;
  discountFactorCache: Map<number, number>;
}

/**
 * Memoized calculation of present value factor for an annuity.
 * PV Factor = [1 - (1 + r)^(-N)] / r
 */
function getMemoizedPVFactor(
  numberOfPayments: number,
  monthlyDiscountRate: number,
  cache: Map<number, number>
): number {
  if (cache.has(numberOfPayments)) {
    return cache.get(numberOfPayments)!;
  }

  const pvFactor =
    (1 - (1 + monthlyDiscountRate) ** -numberOfPayments) / monthlyDiscountRate;

  cache.set(numberOfPayments, pvFactor);
  return pvFactor;
}

/**
 * Memoized calculation of discount factor to current date.
 * Discount Factor = (1 + r)^(-k) where k is months to first payment
 */
function getMemoizedDiscountFactor(
  monthsToFirstPayment: number,
  monthlyDiscountRate: number,
  cache: Map<number, number>
): number {
  if (cache.has(monthsToFirstPayment)) {
    return cache.get(monthsToFirstPayment)!;
  }

  const discountFactor = (1 + monthlyDiscountRate) ** -monthsToFirstPayment;

  cache.set(monthsToFirstPayment, discountFactor);
  return discountFactor;
}

/**
 * Optimized context for strategy calculations that extracts invariant computations.
 */
export interface OptimizationContext {
  earner: Recipient;
  dependent: Recipient;
  earnerFinalDate: MonthDate;
  dependentFinalDate: MonthDate;
  earnerIndex: number;
  dependentIndex: number;
  dependentHasZeroPia: boolean;
  isSpousalBenefitEligible: boolean;
  monthlyDiscountRate: number;
  currentDatePlusOne: MonthDate;
  npvCache: NPVCache;
}

/**
 * Creates an optimization context by pre-computing invariant values.
 */
export function createOptimizationContext(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  monthlyDiscountRate: number
): OptimizationContext {
  // Determine the higher and lower earner based on their Primary Insurance Amount (PIA).
  const classified = classifyEarnerDependent(recipients);
  const earner = classified.earner;
  const dependent = classified.dependent;
  const earnerIndex = classified.earnerIndex;
  const dependentIndex = classified.dependentIndex;
  const earnerFinalDate = finalDates[earnerIndex];
  const dependentFinalDate = finalDates[dependentIndex];

  const dependentHasZeroPia =
    dependent.pia().primaryInsuranceAmount().cents() === 0;
  const isSpousalBenefitEligible = eligibleForSpousalBenefit(dependent, earner);

  return {
    earner,
    dependent,
    earnerFinalDate,
    dependentFinalDate,
    earnerIndex,
    dependentIndex,
    dependentHasZeroPia,
    isSpousalBenefitEligible,
    monthlyDiscountRate,
    currentDatePlusOne: currentDate.addDuration(new MonthDuration(1)),
    npvCache: {
      pvFactorCache: new Map<number, number>(),
      discountFactorCache: new Map<number, number>(),
    },
  };
}

/**
 * Optimized strategy sum calculation using pre-computed context.
 */
function strategySumCentsOptimized(
  currentDate: MonthDate,
  context: OptimizationContext,
  strats: [MonthDuration, MonthDuration]
): number {
  // Extract strategy values based on earner/dependent roles
  const earnerStrat = strats[context.earnerIndex];
  const dependentStrat = strats[context.dependentIndex];

  // Calculate filing dates
  const earnerStratDate = context.earner.birthdate.dateAtSsaAge(earnerStrat);
  let dependentStratDate =
    context.dependent.birthdate.dateAtSsaAge(dependentStrat);

  // If the dependent has 0 PIA, then they can't file earlier than the earner.
  // Move the dependent's filing date up to the earner's:
  if (
    context.dependentHasZeroPia &&
    dependentStratDate.lessThan(earnerStratDate)
  ) {
    dependentStratDate = earnerStratDate;
  }

  const periods = strategySumPeriodsOptimized(
    context,
    earnerStratDate,
    dependentStratDate
  );

  let totalNPVCents = 0;

  for (const period of periods) {
    const monthlyPaymentCents = period.amount.cents();

    // Determine the effective start and end dates for payments, considering currentDate
    // Payments are assumed to be received at the end of the month for the previous month's benefits.
    const firstPaymentDate = period.startDate.addDuration(new MonthDuration(1));
    const lastPaymentDate = period.endDate.addDuration(new MonthDuration(1));

    // The effective start of payments for NPV calculation is the later of currentDate + 1 month or firstPaymentDate
    const effectiveStartPaymentDate = MonthDate.max(
      context.currentDatePlusOne,
      firstPaymentDate
    );
    const effectiveEndPaymentDate = lastPaymentDate;

    // If the effective start date is after the effective end date, no payments from this period contribute to NPV.
    if (effectiveStartPaymentDate.greaterThan(effectiveEndPaymentDate)) {
      continue;
    }

    const numberOfPayments =
      effectiveEndPaymentDate.monthsSinceEpoch() -
      effectiveStartPaymentDate.monthsSinceEpoch() +
      1;

    const monthsToFirstPayment =
      effectiveStartPaymentDate.monthsSinceEpoch() -
      currentDate.monthsSinceEpoch();

    totalNPVCents += calculatePeriodNPV(
      monthlyPaymentCents,
      numberOfPayments,
      monthsToFirstPayment,
      context.monthlyDiscountRate,
      context.npvCache.pvFactorCache,
      context.npvCache.discountFactorCache
    );
  }

  return totalNPVCents;
}

/**
 * Optimized version of strategySumPeriods using pre-computed context.
 */
export function strategySumPeriodsOptimized(
  context: OptimizationContext,
  earnerStratDate: MonthDate,
  dependentStratDate: MonthDate
): BenefitPeriod[] {
  const periods: BenefitPeriod[] = [];

  // Determine the start date for survivor benefits. This is the later of:
  // 1. The month after the earner's death date.
  // 2. The dependent's filing date.
  const survivorStartDate = MonthDate.max(
    context.earnerFinalDate.addDuration(new MonthDuration(1)),
    dependentStratDate
  );

  // Determine the dependent's survivor benefit amount:
  let isSurvivorBenefitApplicable = false;
  let survivorBenefitAmount: Money = Money.zero();
  if (context.dependentFinalDate.greaterThan(survivorStartDate)) {
    survivorBenefitAmount = survivorBenefit(
      /*survivor*/ context.dependent,
      /*deceased*/ context.earner,
      /*deceasedFilingDate*/ earnerStratDate,
      /*deceasedDeathDate*/ context.earnerFinalDate,
      /*survivorFilingDate*/ survivorStartDate
    );
    // Note that if the survivor's personal benefit is greater than their
    // survivor benefit, they will not switch to a survivor benefit.
    const dependentFinalPersonalBenefit = benefitOnDate(
      context.dependent,
      /*filingDate*/ dependentStratDate,
      // Add a year to include all late filing credits.
      /*atDate*/ dependentStratDate.addDuration(MonthDuration.OneYear())
    );
    if (dependentFinalPersonalBenefit.cents() < survivorBenefitAmount.cents()) {
      isSurvivorBenefitApplicable = true;
    }
  }

  // Earner's Personal Benefit:
  // Earner is simple as they will never have spousal or survivor benefits:
  PersonalBenefitPeriods(
    context.earner,
    earnerStratDate,
    context.earnerFinalDate,
    periods,
    context.earnerIndex
  );

  // The latest that the dependent will be collecting a personal benefit is the
  // month before starting a survivor benefit.
  let dependentFinalPersonalDate = context.dependentFinalDate;
  if (
    isSurvivorBenefitApplicable &&
    context.dependentFinalDate.greaterThanOrEqual(survivorStartDate)
  ) {
    dependentFinalPersonalDate = survivorStartDate.subtractDuration(
      new MonthDuration(1)
    );
  }

  // Dependent's Personal Benefit:
  PersonalBenefitPeriods(
    context.dependent,
    dependentStratDate,
    dependentFinalPersonalDate,
    periods,
    context.dependentIndex
  );

  // Dependent's Survivor Benefit:
  if (isSurvivorBenefitApplicable) {
    const survivorPeriod = new BenefitPeriod();
    survivorPeriod.amount = survivorBenefitAmount;
    survivorPeriod.startDate = survivorStartDate;
    survivorPeriod.endDate = context.dependentFinalDate;
    survivorPeriod.recipientIndex = context.dependentIndex;
    survivorPeriod.benefitType = BenefitType.Survivor;
    periods.push(survivorPeriod);
  }

  // Dependent's Spousal Benefit:
  if (context.isSpousalBenefitEligible) {
    const spousalPeriod = new BenefitPeriod();
    // The start date for spousal benefits is the later of the two filing dates.
    spousalPeriod.startDate = MonthDate.max(
      earnerStratDate,
      dependentStratDate
    );
    // The end date for spousal benefits is the earlier of when the dependent
    // starts receiving survivor benefits or their own death date.
    spousalPeriod.endDate = MonthDate.min(
      survivorStartDate.subtractDuration(new MonthDuration(1)),
      context.dependentFinalDate
    );

    if (spousalPeriod.endDate.greaterThanOrEqual(spousalPeriod.startDate)) {
      spousalPeriod.amount = spousalBenefitOnDate(
        context.dependent,
        /*spouse=*/ context.earner,
        /*spouseFilingDate=*/ earnerStratDate,
        /*filingDate=*/ dependentStratDate,
        /*atDate=*/ spousalPeriod.startDate
      );
      spousalPeriod.recipientIndex = context.dependentIndex;
      spousalPeriod.benefitType = BenefitType.Spousal;
      periods.push(spousalPeriod);
    }
  }

  return periods;
}

/**
 * A $0-PIA dependent cannot collect spousal benefits until the earner files.
 * If the brute-force optimizer recorded a dependent filing age earlier than
 * the earner's, every such pair produces the same NPV as the (matching-earner)
 * pair, so the reported dependent age would be misleading. Bump it forward.
 *
 * A dependent recorded in `alreadyFiled` is left alone: their filing month is
 * a fact the user entered, not a tie-break the optimizer chose, and the
 * expected-NPV path reports it unchanged. Bumping it here would make the two
 * surfaces disagree about when that person filed.
 */
function clampZeroPiaDepStrategy(
  recipients: [Recipient, Recipient],
  best: [MonthDuration, MonthDuration, number],
  alreadyFiled: AlreadyFiled
): [MonthDuration, MonthDuration, number] {
  const { earnerIndex, dependentIndex } = classifyEarnerDependent(recipients);
  if (alreadyFiled[dependentIndex] !== null) return best;
  const dependent = recipients[dependentIndex];
  if (dependent.pia().primaryInsuranceAmount().cents() !== 0) return best;

  const earner = recipients[earnerIndex];
  const earnerAge = best[earnerIndex] as MonthDuration;
  const depAge = best[dependentIndex] as MonthDuration;
  const earnerFile = earner.birthdate.dateAtSsaAge(earnerAge);
  const depFile = dependent.birthdate.dateAtSsaAge(depAge);
  if (!depFile.lessThan(earnerFile)) return best;

  // Cap at SSA age 70 — dep can't file past their max benefit age. Matters
  // when the earner is younger than the dep and files at 70. Floor at the age
  // the optimizer actually searched: a dependent already past 70 has no age
  // below that available, so the age-70 cap alone would name a filing month
  // they cannot file in.
  const rawAge = dependent.birthdate.ageAtSsaDate(earnerFile).asMonths();
  const newDepAge = new MonthDuration(
    Math.max(depAge.asMonths(), Math.min(rawAge, MAX_BENEFIT_AGE_MONTHS))
  );
  const result: [MonthDuration, MonthDuration, number] = [
    best[0],
    best[1],
    best[2],
  ];
  result[dependentIndex] = newDepAge;
  return result;
}

/**
 * Calculates the optimal filing ages for a couple so as to maximize lifetime
 * benefits as returned by strategySumCents
 *
 * @param {[Recipient, Recipient]} recipients - An array containing the two
 *                                              recipients for whom the strategy
 *                                              results are being calculated.
 * @param {[MonthDate, MonthDate]} finalDates - An array containing the final
 *                                              dates (death dates) for each
 *                                              recipient.
 * @param {MonthDate} currentDate - Today's date.
 * @param {number} discountRate - Rate used for present value calculation. 0
 *                                means no discount.
 * @param {AlreadyFiled} alreadyFiled - Per recipient, the month benefits
 *                                      actually started, or null. A filed
 *                                      recipient is searched at that single
 *                                      age.
 * @returns {[MonthDuration, MonthDuration]} An array containing the optimal
 *                                           filing ages for each recipient.
 */
export function optimalStrategyCouple(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  discountRate: number,
  alreadyFiled: AlreadyFiled = NOT_FILED
): [MonthDuration, MonthDuration, number] {
  let bestStrategy: [MonthDuration, MonthDuration, number] = [
    new MonthDuration(0),
    new MonthDuration(0),
    -1,
  ];

  const range0 = filingAgeRange(recipients[0], currentDate, alreadyFiled[0]);
  const range1 = filingAgeRange(recipients[1], currentDate, alreadyFiled[1]);
  const startFilingDate0: number = range0.earliest.asMonths();
  const startFilingDate1: number = range1.earliest.asMonths();
  const endFilingAge0: number = range0.latest.asMonths();
  const endFilingAge1: number = range1.latest.asMonths();

  for (let i = startFilingDate0; i <= endFilingAge0; ++i) {
    for (let j = startFilingDate1; j <= endFilingAge1; ++j) {
      const strategy: [MonthDuration, MonthDuration] = [
        new MonthDuration(i),
        new MonthDuration(j),
      ];

      const outcome = strategySumCentsCouple(
        recipients,
        finalDates,
        currentDate,
        discountRate,
        strategy
      );
      if (outcome > bestStrategy[2]) {
        bestStrategy = [strategy[0], strategy[1], outcome];
      }
    }
  }

  return clampZeroPiaDepStrategy(recipients, bestStrategy, alreadyFiled);
}

/**
 * Calculates the optimal filing ages for a couple so as to maximize lifetime
 * benefits as returned by strategySumCents
 *
 * @param {[Recipient, Recipient]} recipients - An array containing the two
 *                                              recipients for whom the strategy
 *                                              results are being calculated.
 * @param {[MonthDate, MonthDate]} finalDates - An array containing the final
 *                                              dates (death dates) for each
 *                                              recipient.
 * @param {MonthDate} currentDate - Today's date.
 * @param {number} discountRate - Rate used for present value calculation. 0
 *                                means no discount.
 * @param {AlreadyFiled} alreadyFiled - Per recipient, the month benefits
 *                                      actually started, or null. A filed
 *                                      recipient is searched at that single
 *                                      age.
 * @returns {[MonthDuration, MonthDuration]} An array containing the optimal
 *                                           filing ages for each recipient.
 */
export function optimalStrategyCoupleOptimized(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  discountRate: number,
  alreadyFiled: AlreadyFiled = NOT_FILED
): [MonthDuration, MonthDuration, number] {
  let bestStrategy: [MonthDuration, MonthDuration, number] = [
    new MonthDuration(0),
    new MonthDuration(0),
    -1,
  ];

  // Calculate monthly discount rate once, since it doesn't change during optimization
  const monthlyDiscountRate = calculateMonthlyDiscountRate(discountRate);

  // Pre-compute optimization context to avoid repeated calculations
  const context = createOptimizationContext(
    recipients,
    finalDates,
    currentDate,
    monthlyDiscountRate
  );

  const range0 = filingAgeRange(recipients[0], currentDate, alreadyFiled[0]);
  const range1 = filingAgeRange(recipients[1], currentDate, alreadyFiled[1]);
  const startFilingDate0: number = range0.earliest.asMonths();
  const startFilingDate1: number = range1.earliest.asMonths();

  // Pre-compute final loop bounds to avoid expensive calculations in loops.
  // filingAgeRange, not a literal 70: a recipient past 70 has a single
  // remaining filing age above it, and a literal bound empties the loop.
  // Never below the start: see optimalStrategyCoupleFast. One recipient dying
  // before they could file must not discard the other's filing decision.
  const endFilingAge0: number = Math.max(
    startFilingDate0,
    Math.min(
      range0.latest.asMonths(),
      recipients[0].birthdate.ageAtSsaDate(finalDates[0]).asMonths()
    )
  );
  const endFilingAge1: number = Math.max(
    startFilingDate1,
    Math.min(
      range1.latest.asMonths(),
      recipients[1].birthdate.ageAtSsaDate(finalDates[1]).asMonths()
    )
  );

  for (let i = startFilingDate0; i <= endFilingAge0; ++i) {
    for (let j = startFilingDate1; j <= endFilingAge1; ++j) {
      const strategy: [MonthDuration, MonthDuration] = [
        new MonthDuration(i),
        new MonthDuration(j),
      ];

      const outcome = strategySumCentsOptimized(currentDate, context, strategy);
      /*const outcome = strategySumCents(
        recipients,
        finalDates,
        currentDate,
        discountRate,
        strategy
      );*/
      if (outcome > bestStrategy[2]) {
        bestStrategy = [strategy[0], strategy[1], outcome];
      }
    }
  }

  return clampZeroPiaDepStrategy(recipients, bestStrategy, alreadyFiled);
}

/**
 * Convenience function that returns the strategy sum in cents rather than as a Money object.
 * This is a wrapper around strategySumTotalPeriods that extracts the cents value.
 */
export function strategySumCentsCouple(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  discountRate: number,
  strats: [MonthDuration, MonthDuration]
): number {
  const monthlyDiscountRate = calculateMonthlyDiscountRate(discountRate);

  return strategySumTotalPeriodsCouple(
    recipients,
    finalDates,
    currentDate,
    monthlyDiscountRate,
    strats
  ).cents();
}

/**
 * Calculates the benefit periods for a single recipient given a filing strategy and final date.
 *
 * @param {Recipient} recipient - The recipient.
 * @param {MonthDate} finalDate - The final date (death date).
 * @param {MonthDuration} strat - The filing age.
 * @returns {BenefitPeriod[]} Array of benefit periods.
 */
export function strategySumPeriodsSingle(
  recipient: Recipient,
  finalDate: MonthDate,
  strat: MonthDuration
): BenefitPeriod[] {
  const periods: BenefitPeriod[] = [];
  const filingDate = recipient.birthdate.dateAtSsaAge(strat);

  PersonalBenefitPeriods(
    recipient,
    filingDate,
    finalDate,
    periods,
    0 // recipientIndex is always 0 for single
  );

  return periods;
}

/**
 * Calculates the net present value of all benefit periods for a single
 * recipient.
 *
 * This function calls strategySumPeriodsSingle to get an array of benefit
 * periods, and then calculates the net present value of the payments during
 * those periods, accounting for the duration of each period and discounting
 * future payments.
 *
 * @param {Recipient} recipient - The recipient for whom the strategy results
 *                                are being calculated.
 * @param {MonthDate} finalDate - The final date (death date) for the recipient.
 * @param {MonthDate} currentDate - Today's date, used as the reference for NPV
 *                                  calculation.
 * @param {number} monthlyDiscountRate - Monthly discount rate (pre-calculated
 *                                       from annual rate).
 * @param {MonthDuration} strat - The filing age (as MonthDuration) for the
 *                                recipient.
 * @returns {Money} The net present value of all payments across all periods.
 */
export function strategySumTotalPeriodsSingle(
  recipient: Recipient,
  finalDate: MonthDate,
  currentDate: MonthDate,
  monthlyDiscountRate: number,
  strat: MonthDuration
): Money {
  const periods = strategySumPeriodsSingle(recipient, finalDate, strat);
  let totalNPVCents = 0;

  for (const period of periods) {
    const monthlyPaymentCents = period.amount.cents();

    // Determine the effective start and end dates for payments, considering currentDate
    // Payments are assumed to be received at the end of the month for the previous month's benefits.
    const firstPaymentDate = period.startDate.addDuration(new MonthDuration(1));
    const lastPaymentDate = period.endDate.addDuration(new MonthDuration(1));

    // The effective start of payments for NPV calculation is the later of currentDate + 1 month or firstPaymentDate
    const effectiveStartPaymentDate = MonthDate.max(
      currentDate.addDuration(new MonthDuration(1)),
      firstPaymentDate
    );
    const effectiveEndPaymentDate = lastPaymentDate;

    // If the effective start date is after the effective end date, no payments from this period contribute to NPV.
    if (effectiveStartPaymentDate.greaterThan(effectiveEndPaymentDate)) {
      continue;
    }

    const numberOfPayments =
      effectiveEndPaymentDate.monthsSinceEpoch() -
      effectiveStartPaymentDate.monthsSinceEpoch() +
      1;

    const monthsToFirstPayment =
      effectiveStartPaymentDate.monthsSinceEpoch() -
      currentDate.monthsSinceEpoch();

    totalNPVCents += calculatePeriodNPV(
      monthlyPaymentCents,
      numberOfPayments,
      monthsToFirstPayment,
      monthlyDiscountRate
    );
  }

  return Money.fromCents(totalNPVCents);
}

/**
 * Convenience function that returns the strategy sum in cents rather than as a
 * Money object. This is a wrapper around strategySumTotalPeriodsSingle that
 * extracts the cents value.
 *
 * @param {Recipient} recipient - The recipient.
 * @param {MonthDate} finalDate - The final date (death date).
 * @param {MonthDate} currentDate - Today's date.
 * @param {number} discountRate - Annual discount rate.
 * @param {MonthDuration} strat - The filing age.
 * @returns {number} The total NPV in cents.
 */
export function strategySumCentsSingle(
  recipient: Recipient,
  finalDate: MonthDate,
  currentDate: MonthDate,
  discountRate: number,
  strat: MonthDuration
): number {
  const monthlyDiscountRate = calculateMonthlyDiscountRate(discountRate);

  return strategySumTotalPeriodsSingle(
    recipient,
    finalDate,
    currentDate,
    monthlyDiscountRate,
    strat
  ).cents();
}

/**
 * Calculates the optimal filing age for a single recipient to maximize
 * lifetime benefits (NPV).
 *
 * @param {Recipient} recipient - The recipient.
 * @param {MonthDate} finalDate - The final date (death date).
 * @param {MonthDate} currentDate - Today's date.
 * @param {number} discountRate - Annual discount rate.
 * @returns {[MonthDuration, number]} A tuple containing the optimal filing age and the max NPV in cents.
 */
export function optimalStrategySingle(
  recipient: Recipient,
  finalDate: MonthDate,
  currentDate: MonthDate,
  discountRate: number
): [MonthDuration, number] {
  // Seed only; the guard below guarantees at least one iteration replaces it.
  let bestStrategy: [MonthDuration, number] = [new MonthDuration(0), -1];

  const range = filingAgeRange(recipient, currentDate);
  const startFilingDate: number = range.earliest.asMonths();

  // Filing after death is not a strategy.
  const endFilingAge: number = Math.min(
    range.latest.asMonths(),
    recipient.birthdate.ageAtSsaDate(finalDate).asMonths()
  );

  // The loop below would otherwise run zero times and return its seed value
  // as though it were an answer. Every value of this function's return type
  // is now a real strategy.
  if (endFilingAge < startFilingDate) {
    throw new NoFilingAgeAvailableError(startFilingDate, endFilingAge);
  }

  for (let i = startFilingDate; i <= endFilingAge; ++i) {
    const strategy = new MonthDuration(i);

    const outcome = strategySumCentsSingle(
      recipient,
      finalDate,
      currentDate,
      discountRate,
      strategy
    );
    if (outcome > bestStrategy[1]) {
      bestStrategy = [strategy, outcome];
    }
  }

  return bestStrategy;
}
